import { useEffect, useState } from 'react';
import { Fingerprint, KeyRound, ShieldCheck } from 'lucide-react';
import Modal from './Modal';
import Button from './elements/Button';
import Input from './elements/Input';
import Info from './elements/Info';
import {
  dismissQuickUnlockPrompt,
  isBiometricAvailable,
  registerBiometric,
  setUpPin,
} from '../utils/quickUnlock';

const PIN_LENGTH = 6;

const isValidPin = (value) => /^\d{6}$/.test(value);

const QuickUnlockSetupPrompt = ({ open, accountEmail, onDone }) => {
  const [step, setStep] = useState('choose');
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('choose');
    setPin('');
    setPinConfirm('');
    setError(null);
    isBiometricAvailable().then(setBiometricAvailable);
  }, [open]);

  const handleSkip = () => {
    dismissQuickUnlockPrompt();
    onDone();
  };

  const handleEnableBiometric = async () => {
    setError(null);
    setIsSaving(true);
    try {
      await registerBiometric(accountEmail);
      onDone();
    } catch {
      setError('Could not set up biometric unlock on this device. You can try again from Profile settings, or set a PIN instead.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePin = async (e) => {
    e.preventDefault();
    setError(null);

    if (!isValidPin(pin)) {
      setError(`Enter a ${PIN_LENGTH}-digit PIN.`);
      return;
    }
    if (pin !== pinConfirm) {
      setError('PINs do not match.');
      return;
    }

    setIsSaving(true);
    try {
      await setUpPin(pin);
      onDone();
    } catch {
      setError('Could not save your PIN on this device. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} title="Quick Unlock" onClose={handleSkip} icon={<ShieldCheck />}>
      <div className="w-full max-w-md p-2">
        {step === 'choose' && (
          <>
            <p className="mb-4 text-sm text-primary-dark">
              Skip re-entering your password and verification code every time you reopen the app on this device.
            </p>
            {error && <Info type="error" className="mb-4">{error}</Info>}
            <div className="space-y-3">
              {biometricAvailable && (
                <Button type="primary" className="w-full" onClick={handleEnableBiometric} loading={isSaving} disabled={isSaving}>
                  <Fingerprint className="w-5 h-5" /> Use Face ID / Touch ID
                </Button>
              )}
              <Button type="primary_outline" className="w-full" onClick={() => setStep('pin-create')} disabled={isSaving}>
                <KeyRound className="w-5 h-5" /> Set a {PIN_LENGTH}-digit PIN
              </Button>
              <Button type="anchor" className="w-full" onClick={handleSkip} disabled={isSaving}>
                Maybe later
              </Button>
            </div>
            <p className="mt-4 text-xs text-text-muted">
              This only controls quick access on this device -- your account is still protected by your password and email verification.
            </p>
          </>
        )}

        {step === 'pin-create' && (
          <form onSubmit={handleSavePin} className="space-y-4">
            {error && <Info type="error" className="mb-2">{error}</Info>}
            <Input
              id="quickUnlockPin"
              label={`Enter a ${PIN_LENGTH}-digit PIN`}
              type="password"
              inputMode="numeric"
              maxLength={PIN_LENGTH}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
              placeholder="••••••"
              disabled={isSaving}
              autoFocus
            />
            <Input
              id="quickUnlockPinConfirm"
              label="Confirm PIN"
              type="password"
              inputMode="numeric"
              maxLength={PIN_LENGTH}
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH))}
              placeholder="••••••"
              disabled={isSaving}
            />
            <div className="flex gap-2">
              <Button type="secondary" className="w-full" onClick={() => setStep('choose')} disabled={isSaving}>
                Back
              </Button>
              <Button type="submit" className="w-full" loading={isSaving} disabled={isSaving}>
                Save PIN
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default QuickUnlockSetupPrompt;
